import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  MailCheck,
} from "lucide-react";
import {
  ApiError,
  cancelAccessRequest,
  createAccessRequest,
  getAccessRequestOptions,
  getAppSettings,
  getAuthMode,
  getMyAccessRequests,
  register,
  requestMeEmailVerification,
  setAuthToken,
} from "@/lib/api";
import {
  ConsentPanel,
  EMPTY_CONSENTS,
  requiredConsentsMet,
} from "@/features/onboarding/components/ConsentPanel";
import type { InviteConsents } from "@/lib/types";
import { publicAppSettingsKey, unscopedKey } from "@/lib/queryClient";
import { useMe } from "@/features/auth/useMe";
import { AuthProductShell } from "@/features/auth/components/AuthProductShell";
import { Button } from "@/components/ui/Button";
import { TkticSelect } from "@/components/ui/TkticSelect";
import type { AccessRequestRole } from "@/lib/types";
import { coerceBool } from "@/lib/coerce";
import {
  authSearchParam,
  isLegacyRequested,
  sanitizeNextPath,
} from "@/features/auth/authPaths";
import { KratosRegisterPanel } from "@/features/auth/kratos/KratosRegisterPanel";
import { teamSeasonLabel, withSlugWhenShared } from "@/lib/teamSeasonLabel";

const ROLE_OPTIONS: Array<{ value: AccessRequestRole; label: string }> = [
  { value: "guardian", label: "Guardian / parent" },
  { value: "player", label: "Player" },
  { value: "team_admin", label: "Team admin" },
  { value: "coach", label: "Coach" },
  { value: "assistant_coach", label: "Assistant coach" },
];

export function RegisterPage() {
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ from: "/register" });
  // A club admin has an account and administers the club — they have nothing
  // to "register" or request here. If they land on /register (e.g. via the
  // post-login /hub path before that recognized club_admin, or a stray link),
  // send them to their admin console instead of the request-team-access form.
  const isClubAdmin = !!me.data?.is_club_admin;
  useEffect(() => {
    if (isClubAdmin) {
      void navigate({ to: "/admin", replace: true });
    }
  }, [isClubAdmin, navigate]);
  const [devToken, setDevToken] = useState<string | null>(null);
  const authMode = useQuery({
    queryKey: unscopedKey("auth-mode"),
    queryFn: getAuthMode,
    staleTime: 60_000,
  });
  const appSettings = useQuery({
    queryKey: publicAppSettingsKey(),
    queryFn: getAppSettings,
    staleTime: 30_000,
  });
  const settings = appSettings.data?.settings;
  const settingsReady = appSettings.isSuccess || appSettings.isError;
  const selfServiceEnabled = appSettings.isError
    ? true
    : settingsReady
      ? boolSetting(settings, "onboarding_self_service_enabled", true)
      : false;
  const socialLoginEnabled =
    settingsReady && !appSettings.isError
      ? boolSetting(settings, "onboarding_social_login_enabled", false)
      : false;
  const googleEnabled = settingsReady
    ? boolSetting(settings, "onboarding_google_enabled", true)
    : false;
  const appleEnabled = settingsReady
    ? boolSetting(settings, "onboarding_apple_enabled", true)
    : false;
  const socialReturnRequested = authSearchParam("social") === "1";
  // `?provider=…&social-start=1` — a club-host social click hands the OIDC
  // registration ceremony to the apex (continuity cookie must be same-origin
  // with the callback). On the apex, auto-start that provider.
  const socialStartProvider =
    authSearchParam("social-start") === "1"
      ? (() => {
          const p = authSearchParam("provider");
          return p === "google" || p === "apple" ? p : null;
        })()
      : null;
  const registered = !!me.data?.user;
  const verified = !!me.data?.profile?.email_verified_at;
  const kratosEnabled = authMode.data?.kratos_enabled ?? false;
  const legacyEnabled = authMode.data
    ? authMode.data.legacy_enabled
    : authMode.isError;
  const legacyRequested =
    isLegacyRequested(search.legacy) ||
    isLegacyRequested(authSearchParam("legacy"));
  const showKratosPanel = kratosEnabled && !legacyRequested;
  const showLegacyCreateForm =
    legacyEnabled && (!kratosEnabled || legacyRequested);
  const registrationClosed = !registered && settingsReady && !selfServiceEnabled;
  const accessRequestsClosed =
    registered && verified && settingsReady && !selfServiceEnabled;
  const shellTitle = registrationClosed
    ? "Registration closed"
    : accessRequestsClosed
      ? "Access requests closed"
      : "Create account";
  const shellDescription = registrationClosed || accessRequestsClosed
    ? "Contact your club admin for an invite or membership update."
    : kratosEnabled
      ? "Create a login, verify it with a one-time code, then request team access for the role you need."
      : "Create a login, verify your email, then request team access for the role you need.";

  return (
    <AuthProductShell
      testId="register-page"
      title={shellTitle}
      description={shellDescription}
    >
      {!registered ? (
        <div className="flex flex-col gap-4">
          {authMode.isPending || !settingsReady ? (
            <p
              data-testid="register-auth-mode-loading"
              className="font-mono text-xs text-ink-muted"
            >
              Checking account options...
            </p>
          ) : null}
          {settingsReady && !selfServiceEnabled ? (
            <OnboardingClosed mode="account" />
          ) : settingsReady && showKratosPanel ? (
            <KratosRegisterPanel
              nextPath={typeof search.next === "string" ? sanitizeNextPath(search.next) : undefined}
              autoExchange={socialReturnRequested}
              socialStartProvider={socialStartProvider}
              socialLoginEnabled={socialLoginEnabled}
              googleEnabled={googleEnabled}
              appleEnabled={appleEnabled}
            />
          ) : null}
          {settingsReady && selfServiceEnabled && showLegacyCreateForm ? (
            <CreateAccountForm
              onRegistered={async (verificationToken) => {
                setDevToken(verificationToken ?? null);
                await queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
              }}
            />
          ) : null}
        </div>
      ) : !verified ? (
        <VerifyGate
          email={me.data?.profile?.email ?? ""}
          devToken={devToken}
          onRefresh={() =>
            queryClient.invalidateQueries({ queryKey: unscopedKey("me") })
          }
        />
      ) : !settingsReady ? (
        <p
          data-testid="register-onboarding-loading"
          className="font-mono text-xs text-ink-muted"
        >
          Checking onboarding options...
        </p>
      ) : !selfServiceEnabled ? (
        <OnboardingClosed mode="access" />
      ) : (
        <AccessRequestForm />
      )}
    </AuthProductShell>
  );
}

function CreateAccountForm({
  onRegistered,
}: {
  onRegistered: (verificationToken?: string) => void | Promise<void>;
}) {
  const queryClient = useQueryClient();
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      register({
        username: username.trim(),
        display_name: displayName.trim() || undefined,
        email: email.trim(),
        password,
      }),
    onSuccess: async (resp) => {
      setAuthToken(resp.token);
      setPassword("");
      await queryClient.invalidateQueries();
      await onRegistered(resp.verification_token);
    },
    onError: (err) => setError(formatErr(err)),
  });

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-start gap-3 border-l-2 border-accent pl-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-accent text-accent-ink">
          <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
        </span>
        <div>
          <h2 className="font-display text-base font-semibold text-ink">
            Start with your login identity
          </h2>
          <p className="mt-1 text-xs leading-5 text-ink-muted">
            Team permissions are requested after email verification, so new
            accounts never land in a default team by accident.
          </p>
        </div>
      </div>
      <form
        data-testid="register-form"
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          if (!username.trim() || !email.trim() || password.length < 8) {
            setError("Username, email, and an 8 character password are required.");
            return;
          }
          mut.mutate();
        }}
      >
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Username
          </span>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            data-testid="register-username"
            className="rounded border border-rule bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Display name
          </span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoComplete="name"
            data-testid="register-display-name"
            className="rounded border border-rule bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs sm:col-span-2">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Email
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            data-testid="register-email"
            className="rounded border border-rule bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs sm:col-span-2">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Password
          </span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            data-testid="register-password"
            className="rounded border border-rule bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
        {error ? (
          <p
            data-testid="register-error"
            className="font-mono text-xs text-destructive sm:col-span-2"
          >
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-between gap-3 sm:col-span-2">
          <Link to="/login" className="text-xs text-ink-muted hover:text-ink">
            Already have an account?
          </Link>
          <Button
            type="submit"
            disabled={mut.isPending}
            data-testid="register-submit"
          >
            {mut.isPending ? (
              "Creating…"
            ) : (
              <>
                Create account
                <ArrowRight aria-hidden="true" />
              </>
            )}
          </Button>
        </div>
      </form>
    </section>
  );
}

function VerifyGate({
  email,
  devToken,
  onRefresh,
}: {
  email: string;
  devToken: string | null;
  onRefresh: () => void;
}) {
  const [sentToken, setSentToken] = useState<string | null>(devToken);
  useEffect(() => {
    setSentToken(devToken);
  }, [devToken]);
  const mut = useMutation({
    mutationFn: requestMeEmailVerification,
    onSuccess: (resp) => {
      if (resp.verification_token) setSentToken(resp.verification_token);
    },
  });
  const verifyUrl = sentToken
    ? `/verify-email?token=${encodeURIComponent(sentToken)}`
    : "/verify-email";

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded bg-accent text-accent-ink">
            <MailCheck aria-hidden="true" className="h-4 w-4" />
          </span>
          <h2 className="font-display text-lg font-semibold text-ink">
            Verify your email
          </h2>
        </div>
        <p className="text-sm text-ink-muted">
          We sent a verification email to{" "}
          <span className="font-mono text-ink">{email || "your email"}</span>.
          Team access requests open after verification.
        </p>
        {sentToken ? (
          <p
            data-testid="register-dev-verification"
            className="rounded border border-rule bg-elevated p-2 font-mono text-xs text-ink"
          >
            Dev verification link:{" "}
            <Link to="/verify-email" search={{ token: sentToken }} className="text-accent">
              {verifyUrl}
            </Link>
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
            data-testid="register-resend-verification"
          >
            {mut.isPending ? "Sending…" : "Resend verification"}
          </Button>
          <Button
            type="button"
            onClick={onRefresh}
            data-testid="register-refresh-verification"
          >
            I verified my email
          </Button>
        </div>
      </div>
    </section>
  );
}

function OnboardingClosed({ mode }: { mode: "account" | "access" }) {
  return (
    <section
      data-testid={`register-onboarding-closed-${mode}`}
      className="rounded border border-rule bg-canvas p-4"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-elevated text-ink-muted">
          <Clock3 aria-hidden="true" className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-lg font-semibold text-ink">
            {mode === "account" ? "Account registration is closed" : "Access requests are closed"}
          </h2>
          <p className="mt-1 text-sm leading-5 text-ink-muted">
            Contact your club admin for an invite or membership update.
          </p>
          <Link
            to="/login"
            className="mt-3 inline-flex rounded text-xs font-mono text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    </section>
  );
}

function AccessRequestForm() {
  const queryClient = useQueryClient();
  const [teamId, setTeamId] = useState("");
  const [role, setRole] = useState<AccessRequestRole>("guardian");
  const [playerId, setPlayerId] = useState("");
  const [relationship, setRelationship] = useState<"parent" | "guardian" | "family">("guardian");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const options = useQuery({
    queryKey: unscopedKey("access-request-options", { teamId: teamId || "" }),
    queryFn: () => getAccessRequestOptions(teamId || undefined),
  });
  const requests = useQuery({
    queryKey: unscopedKey("my-access-requests"),
    queryFn: getMyAccessRequests,
  });
  const teams = options.data?.teams ?? [];
  const players = options.data?.players ?? [];

  useEffect(() => {
    if (!teamId && teams.length === 1) {
      setTeamId(teams[0].id);
    }
  }, [teamId, teams]);

  const teamOptions = useMemo(
    // Each offered team is a current season team (#908 D1): the season
    // tells same-named teams apart, and the slug any two it doesn't.
    () =>
      withSlugWhenShared(
        teams.map((team) => ({
          value: team.id,
          label: teamSeasonLabel(team.name, team.season?.name),
          slug: team.slug,
        })),
      ).map(({ value, label }) => ({ value, label })),
    [teams],
  );
  const playerOptions = useMemo(
    () => [
      { value: "", label: "No player link yet" },
      ...players.map((player) => ({
        value: player.id,
        label: `${player.jersey_number ? `#${player.jersey_number} ` : ""}${player.display_name}`,
      })),
    ],
    [players],
  );

  // Consent is captured from the family member making the request — the
  // approving coach cannot affirm on their behalf, and approval refuses a
  // request that carries none. Per-child items only when a guardian is
  // linking to a player (a player linking to their own record is not
  // consenting for anyone else).
  const [consents, setConsents] = useState<InviteConsents>(EMPTY_CONSENTS);
  const consentPlayerCount = role === "guardian" && playerId ? 1 : 0;
  const consentsReady = requiredConsentsMet(consents, consentPlayerCount);

  const mut = useMutation({
    mutationFn: () =>
      createAccessRequest({
        team_id: teamId,
        requested_role: role,
        requested_player_id: playerId || undefined,
        requested_relationship:
          role === "guardian" && playerId ? relationship : undefined,
        requester_note: note,
        consents,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: unscopedKey("my-access-requests") });
    },
    onError: (err) => setError(formatErr(err)),
  });
  // A request whose team was archived since (#908: its season ended) can
  // no longer be approved; cancelling it brings back the form for the
  // current season's team.
  const cancel = useMutation({
    mutationFn: (id: string) => cancelAccessRequest(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: unscopedKey("my-access-requests") });
    },
    onError: (err) => setError(formatErr(err)),
  });

  const existing = requests.data?.find((req) => req.status === "pending");
  const approved = requests.data?.find((req) => req.status === "approved");
  if (existing) {
    return (
      <section
        data-testid="register-access-pending"
        className="rounded border border-rule bg-canvas p-4"
      >
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded bg-accent/15 text-accent">
            <Clock3 aria-hidden="true" className="h-4 w-4" />
          </span>
          <h2 className="font-display text-lg font-semibold text-ink">
            Request pending
          </h2>
        </div>
        <p className="mt-1 text-sm text-ink-muted">
          {existing.team.archived
            ? `The season for ${teamSeasonLabel(existing.team.name, existing.team.season_name)} has ended, so this request can't be approved. Cancel it to request the current season's team.`
            : `Your request for ${teamSeasonLabel(existing.team.name, existing.team.season_name)} is waiting for a club admin.`}
        </p>
        {existing.team.archived ? (
          <Button
            type="button"
            variant="secondary"
            className="mt-3"
            data-testid="register-access-cancel"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate(existing.id)}
          >
            {cancel.isPending ? "Cancelling…" : "Cancel request"}
          </Button>
        ) : null}
        {error ? (
          <p className="mt-2 font-mono text-xs text-destructive" data-testid="register-access-error">
            {error}
          </p>
        ) : null}
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-3">
      {approved && <p className="text-xs text-ink-muted">Your access to {teamSeasonLabel(approved.team.name, approved.team.season_name)} was approved. <Link to="/hub" className="text-accent underline">Continue to Tktic</Link>.</p>}
      <form
        data-testid="register-access-form"
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          if (!teamId) {
            setError("Pick a team.");
            return;
          }
          mut.mutate();
        }}
      >
        <div className="rounded border border-rule bg-canvas p-3">
          <h2 className="font-display text-lg font-semibold text-ink">
            Request team access
          </h2>
          <p className="mt-1 text-xs leading-5 text-ink-muted">
            Pick the team and role you need. Club admins review requests before
            assigning memberships or player links.
          </p>
        </div>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Team
          </span>
          <TkticSelect
            value={teamId}
            onChange={(next) => {
              setTeamId(next);
              setPlayerId("");
            }}
            options={teamOptions}
            placeholder={options.isLoading ? "Loading teams…" : "Pick a team"}
            ariaLabel="Request team"
            data-testid="register-access-team"
          />
        </label>
        <div className="flex flex-col gap-1">
          <span className="font-display text-xs uppercase tracking-wider text-ink-muted">
            Role
          </span>
          <div className="flex flex-wrap gap-1">
            {ROLE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  setRole(opt.value);
                  if (opt.value !== "guardian" && opt.value !== "player") {
                    setPlayerId("");
                  }
                }}
                data-active={role === opt.value ? "true" : undefined}
                data-testid={`register-access-role-${opt.value}`}
                className="rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink-muted transition-colors hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        {role === "guardian" || role === "player" ? (
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-display uppercase tracking-wider text-ink-muted">
              Player link
            </span>
            <TkticSelect
              value={playerId}
              onChange={setPlayerId}
              options={playerOptions}
              placeholder="No player link yet"
              ariaLabel="Requested player"
              data-testid="register-access-player"
            />
          </label>
        ) : null}
        {role === "guardian" && playerId ? (
          <div className="flex flex-col gap-1">
            <span className="font-display text-xs uppercase tracking-wider text-ink-muted">
              Relationship
            </span>
            <div className="flex flex-wrap gap-1">
              {(["guardian", "parent", "family"] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setRelationship(value)}
                  data-active={relationship === value ? "true" : undefined}
                  className="rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink-muted transition-colors hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
                >
                  {value}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Note to admin
          </span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
            data-testid="register-access-note"
            className="min-h-24 rounded border border-rule bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
        <ConsentPanel
          playerCount={consentPlayerCount}
          consents={consents}
          onChange={setConsents}
        />
        {error ? (
          <p className="font-mono text-xs text-destructive" data-testid="register-access-error">
            {error}
          </p>
        ) : null}
        <Button
          type="submit"
          disabled={mut.isPending || !consentsReady}
          data-testid="register-access-submit"
          className="self-end"
        >
          {mut.isPending ? (
            "Submitting…"
          ) : (
            <>
              Submit request
              <ArrowRight aria-hidden="true" />
            </>
          )}
        </Button>
      </form>
    </section>
  );
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Network error. Try again.";
}

function boolSetting(
  settings: Record<string, unknown> | undefined,
  key: string,
  fallback: boolean,
): boolean {
  if (!settings || settings[key] === undefined || settings[key] === null) {
    return fallback;
  }
  return coerceBool(settings[key]);
}
